import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    Stack,
    Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { MentorshipCompactListSkeleton } from '@/components/admin/mentorship/common/MentorshipLoadingSkeleton';

import { MentorshipEmptyState } from '@/components/admin/mentorship/common/MentorshipEmptyState';
import {
    MentorshipMemberPageHeader,
    MentorshipMemberSurface,
} from '@/modules/mentorship/common/layout/MentorshipMemberPage';
import { MentorshipModuleIcon } from '@/modules/mentorship/common/presentation/MentorshipModulePresentation';

import { useMentorshipNotifications } from './useMentorshipNotifications';

const eventDate = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

export const MentorshipNotificationBadge: React.FC<{
  count?: number;
}> = ({ count = 0 }) =>
  count > 0 ? (
    <Badge colorScheme="red" borderRadius="full" minW="20px" textAlign="center">
      {count > 99 ? '99+' : count}
    </Badge>
  ) : null;

export const MentorshipNotificationSummaryCard: React.FC<{
  href: string;
  title?: string;
}> = ({ href, title = 'Notifications' }) => {
  const router = useRouter();
  const { notifications, loading } = useMentorshipNotifications({ limit: 5 });

  return (
    <MentorshipMemberSurface
      as="button"
      w="full"
      h="full"
      textAlign="left"
      onClick={() => router.push(href)}
      _hover={{ borderColor: 'primary.200', boxShadow: 'md' }}
    >
      <Flex justify="space-between" align="start" gap="3">
        <Flex gap="3" align="center">
          <Flex
            boxSize="34px"
            borderRadius="md"
            bg="orange.50"
            color="orange.500"
            align="center"
            justify="center"
          >
            <MentorshipModuleIcon module="notifications" boxSize="18px" />
          </Flex>
          <Box>
            <Text fontWeight="700" color="gray.800">
              {title}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {loading
                ? 'Loading updates…'
                : notifications?.unreadCount
                ? `${notifications.unreadCount} unread update${
                    notifications.unreadCount === 1 ? '' : 's'
                  }`
                : 'You are all caught up'}
            </Text>
          </Box>
        </Flex>
        <MentorshipNotificationBadge count={notifications?.unseenCount} />
      </Flex>
    </MentorshipMemberSurface>
  );
};

export const MentorshipNotifications: React.FC<{
  title?: string;
  subtitle?: string;
  programId?: string;
  workspaceId?: string;
  embedded?: boolean;
  limit?: number;
}> = ({
  title = 'Notifications',
  subtitle = 'Live Mentorship updates relevant to you.',
  programId,
  workspaceId,
  embedded = false,
  limit = 50,
}) => {
  const router = useRouter();
  const { notifications, loading, error, markSeen, markRead, markingRead } =
    useMentorshipNotifications({ programId, workspaceId, limit });
  const seenForSnapshot = React.useRef('');
  const eventIds = (notifications?.results ?? []).map((item) => item.event._id);
  const unseenIds = (notifications?.results ?? [])
    .filter((item) => !item.isSeen)
    .map((item) => item.event._id);

  React.useEffect(() => {
    const snapshot = unseenIds.join(':');
    if (!snapshot || snapshot === seenForSnapshot.current) return;
    seenForSnapshot.current = snapshot;
    void markSeen(unseenIds);
  }, [markSeen, unseenIds]);

  const content = (
    <Stack spacing="3">
      {error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error.message}
        </Alert>
      ) : null}
      {loading && !notifications ? (
        <MentorshipCompactListSkeleton rows={3} avatar />
      ) : notifications?.results.length ? (
        <>
          <Flex justify="space-between" align="center" mb="1">
            <Text fontSize="sm" color="gray.500">
              {notifications.unreadCount} unread of {notifications.totalCount}
            </Text>
            {notifications.unreadCount ? (
              <Button
                size="xs"
                variant="ghost"
                isLoading={markingRead}
                onClick={() => markRead(eventIds)}
              >
                Mark all read
              </Button>
            ) : null}
          </Flex>
          {notifications.results.map((item) => (
            <Box
              key={item.event._id}
              as="button"
              w="full"
              textAlign="left"
              borderWidth="1px"
              borderColor={item.isRead ? 'gray.100' : 'blue.200'}
              bg={item.isRead ? 'white' : 'blue.50'}
              borderRadius="md"
              p="4"
              onClick={async () => {
                if (!item.isRead) await markRead([item.event._id]);
                if (item.event.deepLink) {
                  await router.push(item.event.deepLink);
                }
              }}
            >
              <Flex justify="space-between" gap="3" align="start">
                <Box minW="0">
                  <Flex align="center" gap="2" mb="1">
                    {!item.isRead ? (
                      <Box boxSize="8px" borderRadius="full" bg="blue.500" />
                    ) : null}
                    <Text fontWeight="700" color="gray.800">
                      {item.event.title}
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    {item.event.message}
                  </Text>
                </Box>
                <Badge flexShrink={0} textTransform="capitalize">
                  {item.event.resourceType.replace(/_/g, ' ')}
                </Badge>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt="2">
                {eventDate(item.event.occurredAt)}
              </Text>
            </Box>
          ))}
        </>
      ) : (
        <MentorshipEmptyState
          compact
          icon="application"
          title="No Mentorship notifications"
          description="New application, Program, Workspace, goal, task, session, resource, and discussion updates will appear here live."
        />
      )}
    </Stack>
  );

  if (embedded) return content;

  return (
    <Stack spacing="5">
      <MentorshipMemberPageHeader title={title} subtitle={subtitle} />
      <MentorshipMemberSurface>{content}</MentorshipMemberSurface>
    </Stack>
  );
};

export const MentorshipNotificationNavLabel: React.FC<{
  label?: string;
}> = ({ label = 'Notifications' }) => {
  const { notifications } = useMentorshipNotifications({ limit: 1 });
  return (
    <Flex as="span" align="center" justify="space-between" gap="2" w="full">
      <Text as="span">{label}</Text>
      <MentorshipNotificationBadge count={notifications?.unseenCount} />
    </Flex>
  );
};
